import {
  DatePicker,
  FormField,
  Select,
  Input,
  SpaceBetween,
  ColumnLayout,
  Checkbox,
  RadioGroup,
  Grid,
} from '@cloudscape-design/components';
import { recurrenceOptions, ScheduleType, weekDays } from 'src/contstant/commonLabels';
import {
  EndConditionType,
  RecurrenceType,
  ScheduleDetails,
} from 'src/types/helios/AuditSchedule';
import { ScheduleValidationPaths } from 'src/validations/AuditScheduleValidator';
import { formatDate } from 'src/utils/generalUtils';

interface ScheduleDatesProps {
  scheduleType: ScheduleType;
  scheduleDetails: ScheduleDetails;
  fieldValidationError: Record<string, string>;
  onChange: (details: ScheduleDetails) => void;
  disabled?: boolean;
}

const ScheduleDates = ({
  scheduleType,
  scheduleDetails,
  fieldValidationError,
  onChange,
  disabled = false,
}: ScheduleDatesProps) => {
  if (scheduleType === 'ONCE') {
    return (
      <FormField
        label="Schedule Date"
        errorText={fieldValidationError[ScheduleValidationPaths.ONE_TIME_DATE.path]}
      >
        <DatePicker
          placeholder="YYYY/MM/DD"
          data-testid="date-picker"
          disabled={disabled}
          value={formatDate(scheduleDetails?.oneTime?.date)}
          onChange={({ detail }) =>
            onChange({
              oneTime: { date: detail.value },
            })
          }
          isDateEnabled={(date) => date >= new Date(new Date().setHours(0, 0, 0, 0))}
        />
      </FormField>
    );
  }

  if (!scheduleDetails?.recurring) {
    return null;
  }

  const recurring = scheduleDetails.recurring;
  const pattern = recurring.recurrencePattern;

  return (
    <SpaceBetween direction="vertical" size="m">
      <FormField
        label="End Condition"
        errorText={
          fieldValidationError[ScheduleValidationPaths.RECURRING_END_CONDITION_TYPE.path]
        }
      >
        <RadioGroup
          items={[
            { value: EndConditionType.NEVER, label: 'Never' },
            { value: EndConditionType.AFTER, label: 'After number of occurrences' },
            { value: EndConditionType.ON, label: 'On specific date' },
          ]}
          value={recurring.endCondition.type}
          onChange={({ detail }) =>
            onChange({
              recurring: {
                ...recurring,
                endCondition: {
                  type: detail.value as any,
                },
              },
            })
          }
        />
      </FormField>
      <SpaceBetween direction="horizontal" size="m">
        <FormField
          label="Schedule Start Date"
          errorText={
            fieldValidationError[ScheduleValidationPaths.RECURRING_START_DATE.path]
          }
        >
          <DatePicker
            data-testid="start-date-picker"
            placeholder="YYYY/MM/DD"
            disabled={disabled}
            value={formatDate(recurring.startDate)}
            isDateEnabled={(date) => date >= new Date(new Date().setHours(0, 0, 0, 0))}
            onChange={({ detail }) =>
              onChange({
                recurring: {
                  ...recurring,
                  startDate: detail.value,
                  endCondition: {
                    ...recurring.endCondition,
                    endDate: undefined,
                  },
                },
              })
            }
          />
        </FormField>
        {recurring.endCondition.type === 'ON' && (
          <FormField
            label="Schedule End Date"
            errorText={
              fieldValidationError[ScheduleValidationPaths.RECURRING_END_DATE.path]
            }
          >
            <DatePicker
              data-testid="end-date-picker"
              placeholder="YYYY/MM/DD"
              value={formatDate(recurring.endCondition.endDate)}
              isDateEnabled={(date) => {
                if (recurring?.startDate) {
                  return (
                    date >
                    new Date(
                      new Date(Date.parse(formatDate(recurring?.startDate))).setHours(
                        0,
                        0,
                        0,
                        0,
                      ),
                    )
                  );
                }
                return date > new Date(new Date().setHours(0, 0, 0, 0));
              }}
              onChange={({ detail }) =>
                onChange({
                  recurring: {
                    ...recurring,
                    endCondition: {
                      ...recurring.endCondition,
                      endDate: detail.value,
                    },
                  },
                })
              }
            />
          </FormField>
        )}
        {recurring.endCondition.type === 'AFTER' && (
          <FormField
            label="Number of Occurrences"
            errorText={
              fieldValidationError[ScheduleValidationPaths.RECURRING_END_OCCURRENCES.path]
            }
          >
            <Input
              type="number"
              value={recurring.endCondition.occurrences?.toString() || ''}
              onChange={({ detail }) =>
                onChange({
                  recurring: {
                    ...recurring,
                    endCondition: {
                      ...recurring.endCondition,
                      occurrences: parseInt(detail.value),
                    },
                  },
                })
              }
            />
          </FormField>
        )}
      </SpaceBetween>

      <SpaceBetween direction="horizontal" size="s">
        <FormField
          label="Audit frequency"
          errorText={
            fieldValidationError[ScheduleValidationPaths.RECURRING_PATTERN_TYPE.path]
          }
        >
          <Select
            selectedOption={{
              label: pattern.type.charAt(0).toUpperCase() + pattern.type.slice(1),
              value: pattern.type,
            }}
            onChange={({ detail }) =>
              onChange({
                recurring: {
                  ...recurring,
                  recurrencePattern: {
                    ...pattern,
                    type: detail.selectedOption.value as RecurrenceType,
                  },
                },
              })
            }
            options={recurrenceOptions}
          />
        </FormField>

        <FormField
          label="Interval"
          errorText={
            fieldValidationError[ScheduleValidationPaths.RECURRING_PATTERN_INTERVAL.path]
          }
        >
          <Input
            type="number"
            value={pattern.interval.toString()}
            onChange={({ detail }) =>
              onChange({
                recurring: {
                  ...recurring,
                  recurrencePattern: {
                    ...pattern,
                    interval: parseInt(detail.value) || 1,
                  },
                },
              })
            }
          />
        </FormField>
      </SpaceBetween>

      {pattern.type === 'WEEKLY' && (
        <FormField
          label="Days of Week"
          errorText={
            fieldValidationError[
              ScheduleValidationPaths.RECURRING_PATTERN_DAYS_OF_WEEK.path
            ]
          }
        >
          <ColumnLayout columns={4}>
            {weekDays.map((day) => (
              <Checkbox
                key={day.value}
                checked={pattern.daysOfWeek?.includes(day.value as any) || false}
                onChange={({ detail }) => {
                  const currentDays = pattern.daysOfWeek || [];
                  const newDays = detail.checked
                    ? [...currentDays, day.value as any]
                    : currentDays.filter((d) => d !== day.value);

                  onChange({
                    recurring: {
                      ...recurring,
                      recurrencePattern: {
                        ...pattern,
                        daysOfWeek: newDays,
                      },
                    },
                  });
                }}
              >
                {day.label}
              </Checkbox>
            ))}
          </ColumnLayout>
        </FormField>
      )}

      {pattern.type === 'MONTHLY' && (
        <FormField
          label="Day of Month"
          errorText={
            fieldValidationError[
              ScheduleValidationPaths.RECURRING_PATTERN_DAY_OF_MONTH.path
            ]
          }
        >
          <Input
            type="number"
            value={pattern.dayOfMonth?.toString() || ''}
            onChange={({ detail }) =>
              onChange({
                recurring: {
                  ...recurring,
                  recurrencePattern: {
                    ...pattern,
                    dayOfMonth: parseInt(detail.value) || undefined,
                  },
                },
              })
            }
          />
        </FormField>
      )}
    </SpaceBetween>
  );
};

export default ScheduleDates;
